import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, onBeforeUnmount, ref, watch, nextTick } from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';
import type { SwiperInstance } from 'tiny-swiper';

import TitleScoreList from '@/components/TitleScoreList.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetGlobalLargeSpotlightTitlesQuery,
	GetGlobalLargeSpotlightTitlesDocument,
	GetGlobalLargeSpotlightTitlesQueryVariables,
} from '@/pages/graphql/queries/GetGlobalLargeSpotlightTitles.query';

import { SnowplowContext, SnowplowOldModuleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { GlobalModuleName } from '@/helpers/composables/useGlobalTitles';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';
import { faChevronLeft, faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

type GlobalLargeSpotlightTitles = NonNullable<GetGlobalLargeSpotlightTitlesQuery['popularTitles']['edges']>;


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalLargeSpotlight',
  props: {
    language: null,
    country: null,
    moduleName: null
  },
  setup(__props: any) {

const props = __props as {
	language: string;
	country: string;
	moduleName: GlobalModuleName;
};

Vue.use(VueObserveVisibility);



const sliderContainer = ref<HTMLElement | null>(null);
const swiperInstance = ref<SwiperInstance | null>(null);
let autoplayTimer: NodeJS.Timeout | null = null;

const queryVariables = computed(() => ({
	country: props.country,
	language: props.language,
	first: 7,
}));

const { result, onError } = useQuery<GetGlobalLargeSpotlightTitlesQuery, GetGlobalLargeSpotlightTitlesQueryVariables>(
	GetGlobalLargeSpotlightTitlesDocument,
	queryVariables,
	{ errorPolicy: 'all' }
);

const globalLargeSpotlightTitles = computed<GlobalLargeSpotlightTitles>(() => result.value?.popularTitles.edges ?? []);

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Global Large Spotlight Titles Data]:',
		{ error: error, where: 'Component: GlobalLargeSpotlight' },
		'error'
	);
});

function getBackdrop(backdrops: { backdropUrl: string }[]) {
	return backdrops[0]?.backdropUrl || '';
}

function getHoursAndMinutes(minutes: number) {
	const hrs = Math.floor(minutes / 60);
	const mins = minutes % 60;

	return `${hrs}h ${mins}m`;
}

async function initSwiper() {
	await nextTick();

	if (!sliderContainer.value || swiperInstance.value) {
		return;
	}

	const { default: TinySwiper } = await import('tiny-swiper');
	const { default: SwiperPluginPagination } = await import('tiny-swiper/lib/modules/pagination.min.js');

	try {
		swiperInstance.value = new TinySwiper(sliderContainer.value, {
			loop: true,
			slideClass: 'swiper-slide',
			wrapperClass: 'swiper-wrapper',
			spaceBetween: 0,
			mousewheel: false,
			touchable: true,
			plugins: [SwiperPluginPagination],
			pagination: {
				el: '.swiper-pagination',
				clickable: true,
				bulletClass: 'swiper-pagination-bullet',
				bulletActiveClass: 'swiper-pagination-bullet-active',
			},
		});

		startAutoplay(true);
	} catch (error) {
		console.error('Swiper initialization error:', error);
	}
}

watch(
	globalLargeSpotlightTitles,
	newTitles => {
		if (newTitles.length > 0 && !swiperInstance.value) {
			initSwiper();
		}
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	if (autoplayTimer) {
		clearInterval(autoplayTimer);
	}
	if (swiperInstance.value) {
		swiperInstance.value.destroy();
	}
});

function startAutoplay(isVisible: boolean) {
	if (isVisible) {
		if (!autoplayTimer && swiperInstance.value) {
			autoplayTimer = setInterval(() => {
				swiperInstance.value?.slideTo(swiperInstance.value.state.index + 1);
			}, 3000);
		}
	} else {
		if (autoplayTimer) {
			clearInterval(autoplayTimer);
			autoplayTimer = null;
		}
	}
}

function observeVisibilityOptions() {
	return {
		...defaultObservableOptions,
		callback: (isVisible: boolean) => {
			startAutoplay(isVisible);

			if (!isVisible) return;
			TrackingHelper.trackEvent(
				ImpressionTrackingEvents.MODULE,
				{
					action: 'impression',
					nonInteraction: true,
				},
				[moduleContext.value]
			);
		},
	};
}

function next() {
	if (swiperInstance.value) {
		swiperInstance.value.slideTo(swiperInstance.value.state.index + 1);
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'slider_clicked',
			label: 'right',
		},
		[moduleContext.value]
	);
}

function previous() {
	if (swiperInstance.value) {
		swiperInstance.value.slideTo(swiperInstance.value.state.index - 1);
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'slider_clicked',
			label: 'left',
		},
		[moduleContext.value]
	);
}

function handleMoreInfoClick(title: any, contexts: SnowplowContext[] = []) {
	const titleContext = TrackingHelper.getTitleContextGraphql(
		title.node.objectId,
		title.node.objectType,
		title?.node?.content?.seasonNumber
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'more_info_clicked',
			label: 'featured',
		},
		[...contexts, titleContext, moduleContext.value]
	);
}

return { __sfc: true,props, sliderContainer, swiperInstance, autoplayTimer, queryVariables, result, onError, globalLargeSpotlightTitles, moduleContext, getBackdrop, getHoursAndMinutes, initSwiper, startAutoplay, observeVisibilityOptions, next, previous, handleMoreInfoClick, TitleScoreList, PictureComp, PosterGraphql, BasicButton, TitleDetailLink, faChevronLeft, faChevronRight, faInfoCircle }
}

})